/**
 * Ties the 90 day supply selling plan to a three unit variant.
 *
 * Pack siblings come from the `custom.pack_skus` product metafield when it is filled
 * in, listing the single unit SKU first and the 90 day supply SKU second. Products
 * without it fall back to the SKU suffix convention, where siblings share a base SKU
 * and differ only by a `-1` or `-3` ending, e.g. `lemonlime-1` and `lemonlime-3`.
 *
 * Picking the 90 day plan swaps the form to the `-3` sibling, every other plan swaps
 * back to `-1`.
 *
 * The swap is driven through the theme's own option inputs, so ProductForm resolves
 * the variant id, price and media exactly as it does for a manual selection and the
 * Skio picker follows along through its existing [name="id"] listener.
 *
 * Any option named "Pack size" is hidden on every product regardless, since quantity
 * is always chosen through the Skio plan picker. Products whose variants carry no SKU,
 * or no sibling, still get the option hidden but are otherwise left untouched.
 */
(() => {
  const PACK_SUFFIX = /-([13])$/
  const SINGLE = '1'
  const TRIPLE = '3'

  // Identifies the 90 day plan by its Skio name, so renaming it in Skio to anything
  // without a "90" in it silently turns this off.
  const NINETY_DAY_PLAN = /90/

  // The pack size option is always hidden, even on products where the 90 day swap
  // pairing below can't be resolved (missing pack_skus metafield, no -1/-3 SKU
  // siblings). Shoppers pick quantity through the Skio plan picker, never here.
  const PACK_SIZE_NAME = /^pack size$/i

  // The two product templates wrap and label an option differently, so both shapes are
  // matched when hiding it.
  const OPTION_WRAPPERS = '.product__variant-selector, .product-form__options-item'
  const OPTION_LABELS = '.product__variant-title, .product-form__item-label'

  // Pack option values, for pairing siblings straight off the option when a product
  // carries neither the pack_skus metafield nor the -1/-3 SKU convention.
  const PACK_VALUES = { one: SINGLE, '1': SINGLE, three: TRIPLE, '3': TRIPLE }

  // Stops a runaway swap loop if plans are mis-assigned in Skio, rather than
  // wedging the page. The budget starts over once the swapping pauses.
  const MAX_SWAPS_PER_INTERACTION = 4
  const SWAP_BUDGET_RESET_MS = 500

  class SkioPackVariant {
    constructor(form, product, packSkus) {
      this.form = form
      this.product = product
      this.variantInput = form.querySelector('[name="id"]')
      this.swaps = 0
      this.lastSwapAt = 0

      this.siblings = this.buildSiblings(packSkus)

      this.optionIndex = this.findPackOptionIndex()
      if (this.optionIndex < 0) return

      this.hidePackOption()

      // Last resort, and only reachable now the pack option is known: pair siblings off
      // the option itself. This covers products carrying neither the metafield nor the
      // SKU convention, which would otherwise never publish a family to the picker and
      // so never show the 90 day plan.
      if (!this.siblings.size) this.siblings = this.siblingsFromOption()

      // Hiding the option happens unconditionally above; wiring up the swap only
      // makes sense once siblings were actually resolved.
      if (!this.siblings.size) return

      this.publishFamily()
      this.bindEvents()
      this.sync(this.currentPlan())
    }

    /**
     * Hands the picker the pair it is currently showing, so it can offer every plan in
     * the family rather than only the ones sold on the selected variant. Without this
     * the 90 day box never renders while the single unit is selected.
     */
    publishFamily() {
      const picker = document.querySelector('skio-plan-picker')
      if (!picker) return

      const family = this.familyFor(this.currentVariant())
      picker.packVariants = family ? [family[SINGLE], family[TRIPLE]] : []
    }

    familyFor(variant) {
      return (variant && this.siblings.get(variant.id)) || null
    }

    buildSiblings(packSkus) {
      const pairs = this.parsePackSkus(packSkus)
      return pairs.length ? this.siblingsFromPairs(pairs) : this.siblingsFromSuffix()
    }

    /**
     * Accepts either the two value form, `["lemonlime-1", "lemonlime-3"]`, or one
     * `single,three` entry per pair for products that need several, such as a gummy
     * sold in more than one flavour.
     */
    parsePackSkus(packSkus) {
      const entries = (Array.isArray(packSkus) ? packSkus : [packSkus]).map(entry => String(entry ?? '').trim()).filter(Boolean)
      const paired = entries.filter(entry => /[,|]/.test(entry))

      if (paired.length) return paired.map(entry => entry.split(/[,|]/).map(sku => sku.trim()))
      if (entries.length === 2) return [entries]
      return []
    }

    variantBySku(sku) {
      const wanted = sku.trim().toLowerCase()
      return this.product.variants.find(variant => (variant.sku || '').trim().toLowerCase() === wanted) || null
    }

    siblingsFromPairs(pairs) {
      const siblings = new Map()

      pairs.forEach(([singleSku, tripleSku]) => {
        const single = singleSku && this.variantBySku(singleSku)
        const triple = tripleSku && this.variantBySku(tripleSku)

        if (!single || !triple) {
          console.warn(`[skio pack variant] pack_skus names "${singleSku}" and "${tripleSku}" but no variant carries one of those SKUs.`)
          return
        }

        const family = { [SINGLE]: single, [TRIPLE]: triple }
        siblings.set(single.id, family)
        siblings.set(triple.id, family)
      })

      return siblings
    }

    // Groups variants that share a base SKU, keyed by every member's variant id so a
    // sibling can be looked up straight from whatever is currently selected.
    siblingsFromSuffix() {
      const byBase = new Map()

      this.product.variants.forEach(variant => {
        const sku = (variant.sku || '').trim()
        const match = PACK_SUFFIX.exec(sku)
        if (!match) return

        const base = sku.slice(0, match.index)
        if (!byBase.has(base)) byBase.set(base, {})
        byBase.get(base)[match[1]] = variant
      })

      return this.pairUp(byBase)
    }

    /**
     * Groups variants that agree on every option but the pack one, so `300mg / One`
     * pairs with `300mg / Three`. Unlike the SKU routes this needs no setup per
     * product, but it can only run once the pack option has been identified.
     */
    siblingsFromOption() {
      const byRest = new Map()

      this.product.variants.forEach(variant => {
        const value = String(variant.options[this.optionIndex] ?? '').trim().toLowerCase()
        const pack = PACK_VALUES[value]
        if (!pack) return

        const rest = variant.options.filter((_, index) => index !== this.optionIndex).join(' / ')
        if (!byRest.has(rest)) byRest.set(rest, {})
        byRest.get(rest)[pack] = variant
      })

      return this.pairUp(byRest)
    }

    // Drops incomplete groups and keys each surviving pair by both members' variant
    // ids, so a sibling can be looked up from whichever one is selected.
    pairUp(groups) {
      const siblings = new Map()

      groups.forEach(family => {
        if (!family[SINGLE] || !family[TRIPLE]) return
        siblings.set(family[SINGLE].id, family)
        siblings.set(family[TRIPLE].id, family)
      })

      return siblings
    }

    /**
     * `product | json` serialises options as plain name strings, matching the order of
     * each variant's `options` array. Object entries are tolerated too, in case a
     * future Shopify version starts serialising the richer shape.
     */
    optionNames() {
      const options = this.product.options || []
      return options.map(option => String(option?.name ?? option ?? '').trim())
    }

    // Prefers the option literally named "Pack size" so it hides even when no sibling
    // pairing could be resolved for this product. Falls back to whichever option
    // differs between two resolved siblings, for products naming it something else.
    findPackOptionIndex() {
      const byName = this.optionNames().findIndex(name => PACK_SIZE_NAME.test(name))
      if (byName >= 0) return byName

      for (const family of this.siblings.values()) {
        const single = family[SINGLE].options
        const triple = family[TRIPLE].options
        const differing = single.reduce((indexes, value, index) => (value === triple[index] ? indexes : indexes.concat(index)), [])

        if (differing.length === 1) return differing[0]
      }

      return -1
    }

    optionInputs() {
      return Array.from(this.form.querySelectorAll(`[data-option="option${this.optionIndex + 1}"]`))
    }

    hidePackOption() {
      const selector = this.packOptionWrapper()

      if (!selector) {
        console.warn('[skio pack variant] "Pack size" is a product option but no wrapper for it was found in the DOM to hide.')
        return
      }

      selector.style.display = 'none'
    }

    /**
     * Locates the block wrapping the pack option, by its input first and by the
     * rendered label second. The two product templates number `data-option`
     * differently (one capitalises it), so the label match is what keeps this working
     * when the attribute lookup misses.
     */
    packOptionWrapper() {
      const fromInput = this.optionInputs()[0]?.closest(OPTION_WRAPPERS)
      if (fromInput) return fromInput

      const labels = Array.from(this.form.querySelectorAll(OPTION_LABELS))
      const label = labels.find(node => PACK_SIZE_NAME.test(node.textContent.replace(/:\s*$/, '').trim()))

      return label?.closest(OPTION_WRAPPERS) || null
    }

    bindEvents() {
      document.addEventListener('skio::update-selling-plan', event => this.sync(event.detail?.sellingPlan))

      // Switching flavour moves to a different pack family, so the picker needs the
      // new pair.
      this.variantInput?.addEventListener('change', () => this.publishFamily())
    }

    currentVariant() {
      const id = Number(this.variantInput?.value)
      return this.product.variants.find(variant => variant.id === id) || null
    }

    // Used once on load: the picker dispatches its first plan event before this script
    // gets a listener attached, so the starting plan is read off the form instead.
    currentPlan() {
      const id = Number(this.form.querySelector('[name="selling_plan"]')?.value)
      if (!id) return null

      return (this.product.selling_plan_groups || []).flatMap(group => group.selling_plans).find(plan => plan.id === id) || null
    }

    hasPlan(variant, planId) {
      return (variant.selling_plan_allocations || []).some(allocation => allocation.selling_plan_id === planId)
    }

    sync(plan) {
      const current = this.currentVariant()
      const family = current && this.siblings.get(current.id)
      if (!family) return

      const target = family[plan && NINETY_DAY_PLAN.test(plan.name || '') ? TRIPLE : SINGLE]
      if (!target || target.id === current.id) return

      // Never move onto a variant the chosen plan is not sold on: Skio would drop the
      // plan on its next render and the two would swap back and forth indefinitely.
      if (plan && !this.hasPlan(target, plan.id)) {
        console.warn(`[skio pack variant] ${target.sku} is not assigned selling plan ${plan.id} in Skio, leaving the variant alone.`)
        return
      }

      // A loop swaps back to back within milliseconds; a shopper moving between plans
      // leaves a gap, so anything unhurried starts the budget over.
      if (Date.now() - this.lastSwapAt > SWAP_BUDGET_RESET_MS) this.swaps = 0

      if (this.swaps >= MAX_SWAPS_PER_INTERACTION) {
        console.warn('[skio pack variant] too many variant swaps in a row, stopping to avoid a loop.')
        return
      }

      this.swaps++
      this.lastSwapAt = Date.now()
      this.selectVariant(target)
    }

    selectVariant(target) {
      const value = target.options[this.optionIndex]
      const inputs = this.optionInputs()
      const radios = inputs.filter(input => input.type === 'radio')
      const active = radios.length ? radios.find(input => input.value === value) : inputs[0]
      if (!active) return

      if (radios.length) {
        active.checked = true
      } else {
        active.value = value
      }

      active.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  function init() {
    const form = document.querySelector('[data-product-form]')
    const productJson = document.querySelector('[data-product-json]')
    const packSkusJson = document.querySelector('[data-pack-skus]')
    if (!form || !productJson) return

    try {
      new SkioPackVariant(form, JSON.parse(productJson.textContent), packSkusJson ? JSON.parse(packSkusJson.textContent) : null)
    } catch (error) {
      console.error('[skio pack variant] failed to initialise: ', error)
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
